import { HTTPError } from "@marketplace/http";
import { notifications } from "@marketplace/ui/notifications";
import {
  useMutation,
  type UseMutationOptions,
  useQueryClient
} from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import type { SetPromoRequestDto } from "../../dtos/set-promo-dto";
import { ProductQueryKeys } from "../../generators/product-query-keys";
import { ProductService } from "../../services/product-service";

type UsePutSetPromoMutationParams = {
  options?: UseMutationOptions<
    { success: boolean },
    unknown,
    SetPromoRequestDto
  >;
};

export const usePutSetPromoMutation = ({
  options
}: UsePutSetPromoMutationParams = {}) => {
  const queryClient = useQueryClient();
  const { q, page, sort, draft_page, draft_q, draft_sort } = useSearch({
    from: "/products/list/"
  });
  const { t } = useTranslation("product");

  return useMutation({
    mutationFn: async (payload) => {
      return await ProductService.setPromo({ payload });
    },
    onSuccess: async (...args) => {
      const [, variable] = args;

      const currentPageKey = ProductQueryKeys.getProductList({
        limit: 10,
        page,
        q,
        sort
      }).queryKey;

      await queryClient.invalidateQueries({ queryKey: currentPageKey });

      const draftListKey = ProductQueryKeys.getProductDraftList({
        limit: 10,
        page: draft_page,
        q: draft_q,
        sort: draft_sort
      }).queryKey;

      await queryClient.invalidateQueries({ queryKey: draftListKey });

      const messageKey = variable.is_promo_available
        ? "notifications.successActivatePromo"
        : "notifications.successDeactivatePromo";

      notifications.show({
        message: t(messageKey)
      });

      options?.onSuccess?.(...args);
    },
    onError: (...errorArgs) => {
      const [error, variable] = errorArgs;
      if (error instanceof HTTPError) {
        const messageKey = variable.is_promo_available
          ? "notifications.failedToActivatePromo"
          : "notifications.failedToDeactivatePromo";

        notifications.show({
          variant: "error",
          message: t(messageKey)
        });
      }

      options?.onError?.(...errorArgs);
    },
    ...options
  });
};
